<template>
  <div class="step">
    <div class="step-header">
      <div class="step-icon">
        <v-icon size="24" color="#e8612f">mdi-receipt-outline</v-icon>
      </div>
      <div class="step-content">
        <h2 class="title">Tax setup</h2>
        <p class="desc">
          Set up your service tax rate.
        </p>
      </div>
    </div>

    <v-form class="form">
      <AppTextField
        v-model="serviceTax"
        type="number"
        label="Service Tax (%)"
        label-icon=""
        placeholder="e.g. 13"
        :error-messages="serviceTaxErrors"
      />
    </v-form>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";

const props = defineProps<{
  current_step?: number;
  errors?: Record<string, any> | null;
  profileInfo?: any;
}>();

const serviceTax = ref<string | number>("");

watch(
  () => props.profileInfo,
  (info) => {
    const tax = info?.tax_info || null;
    if (!tax) return;

    // Prefill only if user hasn't typed anything.
    if (serviceTax.value === "" && tax?.service_tax != null) {
      serviceTax.value = tax.service_tax;
    }
  },
  { immediate: true },
);

function getErrorsForField(field: string): string[] {
  const errs: any = props.errors;
  if (!errs) return [];

  const root = errs?.errors && typeof errs.errors === "object" ? errs.errors : errs;
  const raw = root?.[field];
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((v) => String(v));
  return [String(raw)];
}

const serviceTaxErrors = computed(() => getErrorsForField("serviceTax"));

function getValues() {
  return {
    serviceTax: serviceTax.value,
  };
}

defineExpose({ getValues });
</script>

<style scoped>
.step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: -0.025em;
}

.desc {
  margin: 0;
  color: var(--color-subtitle-light);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

.form {
  display: grid;
  gap: 20px;
  margin-top: 8px;
}
</style>
